import { FC, useEffect, useState } from "react";
import styles from './bid-overview.module.scss';
import TooltipContainer from "../../components/shared/TooltipContainer/TooltipContainer";
import HTMLParser from "../../components/shared/HTMLParser/HTMLParser";
import BtButton from "../bt-button/bt-button";
import _ from "lodash";
import moment from "moment";
import { BidIntentStatus, bidIntentOptions } from "../page-quotation/BidIntentControls/BidIntentControls";
import { MoreTimeIcon } from '../../components/svgs/moreTimeIcon';
import RequestQuoteIcon from '../../components/svgs/requestQuoteIcon';
import { ReactComponent as OpenedEyeIcon } from '../../assets/svg/openedEyeIcon.svg';
import { ReactComponent as CircleQuestionIcon } from '../../assets/svg/circleQuestionIcon.svg';
import { ReactComponent as CircleDollarIcon } from '../../assets/svg/circleDollarIcon.svg';
import { ReactComponent as CircleXIcon } from '../../assets/svg/circleXIcon.svg';
import { ReactComponent as CircleCheckIcon } from '../../assets/svg/circleCheckIcon.svg';
import { ReactComponent as TriangleWarningIcon } from '../../assets/svg/triangleWarningIcon.svg';
import { ReactComponent as MessageIcon } from '../../assets/svg/messageIcon.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/download2.svg';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { RouteComponentProps, withRouter } from "react-router-dom";
import { formatPrice } from '../../util';
import DownloadButton from "../../components/shared/DownloadButton";
import { ServiceQuotation } from "../../services/service-quotation";
import Spinner from "../../components/shared/Spinner/Spinner";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import FilteringControl from "../../components/shared/FilteringControl/FilteringControl";

type HasId = {
  _id: string;
}

type HasName = {
  name: string;
}

type HasSlug = {
  slug: string;
}

type HasOrgId = {
  orgId: string;
}

type BidIntent = {
  intent: BidIntentStatus;
  declaredBy: HasName;
  declaredAt: string;
  reasonDeclined: string;
}

type Supplier = {
  supplierId: HasId & HasName;
  bidLink: string;
  isSeen: boolean;
  seenAt: string;
  bidIntentStatus: BidIntent;
  isSubmitted: boolean;
  message: string;
  reservations: string;
  attachments: any[];
  date: Date | null;
  quotationValidity: Date | null;
}

type DisplayColumn = {
  supplier: Supplier;
  versionIdx: number;
  refVersion: number;
  isLatest: boolean;
  supplierIndex: number;
}

type Props = RouteComponentProps & {
  modifiedSuppliers: any[];
  versionData: any[];
  showDefaultBidValidityDate: boolean;
  removeDuplicates: (originalArray: any[], prop: string) => any[];
  onCalendar: (item: Supplier, v?: any[], version?: {refVersion : number}) => void;
  getVersionInState: (supplierId: string) => number;
  calcDaysLeft: (date : Date | null) => number;
  currentProject: HasSlug & HasName & HasOrgId;
  areaSlug: string;
  areaName: string;
  getQuotationVersions: (supplierId: {_id: string}) => any[];
}

const BidOverview : FC<Props> = ({
  modifiedSuppliers,
  versionData,
  showDefaultBidValidityDate,
  removeDuplicates,
  onCalendar,
  getVersionInState,
  calcDaysLeft,
  currentProject,
  areaSlug,
  areaName,
  history,
}) => {
  const [t] = useTranslation('projects', { keyPrefix: 'singlePurchase.bidOverview' });
  const keyOfColumn = (col: DisplayColumn) => `${col.supplier.supplierId._id}_v${col.versionIdx}`;
  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
  const [isCollapseAllEnabled, setIsCollapseAllEnabled] = useState(false);
  const [isExpandAllEnabled, setIsExpandAllEnabled] = useState(true);
  const [hoveredSupplierIndex, setHoveredSupplierIndex] = useState<number | null>(null);

  const getSupplierIdsWithMultipleVersions = () => {
    const supplierIdsWithMultipleVersions: string[] = [];
    for (let i = 0; i < modifiedSuppliers.length; i++) {
      const supplierId = modifiedSuppliers[i].supplierId._id;
      const versions = versionData[0]?.versions;
      let versionCount = 0;
      for (let j = 0; j < versions?.length; j++) {
        const { supplierBids } = versions[j];
        if (supplierBids?.find((b: { supplierId: string }) => b.supplierId === supplierId))
          versionCount++;
      }
      if (versionCount > 1) supplierIdsWithMultipleVersions.push(supplierId);
    }
    return supplierIdsWithMultipleVersions;
  }

  useEffect(() => {
    const supplierIdsWithMultipleVersions = getSupplierIdsWithMultipleVersions();
    const allExpanded = supplierIdsWithMultipleVersions.every(id => expandedSuppliers.has(id));
    const noneExpanded = supplierIdsWithMultipleVersions.every(id => !expandedSuppliers.has(id));
    setIsExpandAllEnabled(allExpanded);
    setIsCollapseAllEnabled(noneExpanded);
  }, [expandedSuppliers]);

  const toggleExpandSupplier = (supplierId: string) => {
    setExpandedSuppliers(prev => {
      const next = new Set(prev);
      if (next.has(supplierId)) next.delete(supplierId); else next.add(supplierId);
      return next;
    });
  };

  const getPricesForVersionAndSupplier = (supplierId: string, versionIdx: number) => {
    const quoteVersions = _.head(versionData);
    if (!quoteVersions?.versions?.[versionIdx]) return [];
    const supplierBids = quoteVersions.versions[versionIdx].supplierBids?.find(
      (x: {supplierId: string}) => x.supplierId === supplierId,
    );
    let priceDetails = quoteVersions.versions[versionIdx].priceDetails;

    if (supplierBids?.priceDetails) {
      return supplierBids.priceDetails.map((bids: { price: string, priceType: string }) => {
        const formattedPrice = formatPrice(bids.price);
        priceDetails = priceDetails.filter((pd: { priceType?: HasId }) => pd.priceType);
        const findPriceDetail = priceDetails.find((details: { priceType: HasId }) => details.priceType._id == bids.priceType);
        return {
          name: findPriceDetail?.priceType?.name,
          unit: findPriceDetail?.unit ?? t('fallbackPriceUnit', 'kr'),
          price: formattedPrice ? formattedPrice : '-',
        };
      });
    }
    return priceDetails?.map((p: { priceType: HasName }) => ({ name: p.priceType.name })) ?? [];
  };

  const getDemandsForVersionAndSupplier = (supplierId: string, versionIdx: number) => {
    const quoteVersions = _.head(versionData);
    if (!quoteVersions?.versions?.[versionIdx]) return [];
    const supplierBids = quoteVersions.versions[versionIdx].supplierBids?.find(
      (x: { supplierId: string }) => x.supplierId === supplierId,
    );
    const demands = quoteVersions.versions[versionIdx].demands;
    if (demands && supplierBids?.demands) {
      return demands.map((d: { demandId: HasId & HasName }) => ({
        name: d.demandId.name,
        value: !!supplierBids.demands.find((sd: { demandId: string }) => sd.demandId === d.demandId._id),
      }));
    }
    return demands?.map((d: { demandId: HasId & HasName }) => ({ name: d.demandId.name })) ?? [];
  };

  const getSupplierDataForVersion = (supplier: Supplier, versionIdx: number): Supplier => {
    const quoteVersions = _.head(versionData);
    if (!quoteVersions?.versions?.[versionIdx]) return supplier;

    const versionObj = quoteVersions.versions[versionIdx];
    const supplierBid = versionObj.supplierBids?.find(
      (x: any) => x.supplierId === supplier.supplierId._id,
    );
    const versionSupplierInfo = versionObj.suppliers?.find(
      (s: any) => s.supplierId === supplier.supplierId._id,
    );

    return {
      ...supplier,
      isSeen: versionSupplierInfo?.isSeen,
      seenAt: versionSupplierInfo?.seenAt,
      bidIntentStatus: versionSupplierInfo?.intentStatus,
      isSubmitted: versionSupplierInfo?.isSubmit,
      message: supplierBid?.message ?? '',
    };
  };

  const getQuoteValidity = (validity : Date | null) => {
    return validity ? moment(validity).format('YYYY-MM-DD') : ""
  };

  const getQuotationVersions = (supplierId: {_id: string}) => {
    if (!supplierId) return []; // was null

    const quotationVersions = [];
    const getVersion = !_.isEmpty(versionData) ? versionData[0].versions : undefined;
    if (!_.isEmpty(getVersion)) {
      for (const qv of getVersion) {
        for (const s of qv.suppliers) {
          if (s.supplierId === supplierId._id) quotationVersions.push(qv);
        }
      }
    }

    const quotationVersionsUnique = removeDuplicates(quotationVersions, '_id');
    return quotationVersionsUnique.filter((v: any) => v.supplierBids.some((s: any) => s.supplierId === supplierId._id));
  }

  const getDisplayColumns = (suppliers: Supplier[]): DisplayColumn[] => {
    return suppliers.flatMap((supplier, supplierIndex) => {
      const supplierId = supplier.supplierId._id;
      const versions = getQuotationVersions(supplier.supplierId);
      const isExpanded = expandedSuppliers.has(supplierId);

      if (!isExpanded || versions.length <= 1) {
        const versionIdx = getVersionInState(supplierId) ?? (_.last(versions) as any)?.refVersion ?? 0;
        return [{ supplier, versionIdx, refVersion: versionIdx, isLatest: true, supplierIndex }];
      }

      return [...versions].reverse().map((v: any, i: number) => ({
        supplier,
        versionIdx: v.refVersion,
        refVersion: v.refVersion,
        isLatest: i === 0,
        supplierIndex,
      }));
    });
  };

  const colClass = (col: DisplayColumn, ...extra: (string | undefined | false)[]) =>
    cx( {[`${styles.colGray}`]: col.supplierIndex % 2 === 0}, ...extra) || undefined;

  const colTdProps = (col: DisplayColumn, ...extra: (string | undefined | false)[]) => ({
    className: colClass(col, ...extra),
    onMouseEnter: () => setHoveredSupplierIndex(col.supplierIndex),
    onMouseLeave: () => setHoveredSupplierIndex(null),
  });

  const renderStatusIcons = (supplier: Supplier) => {
    const { supplierId } = supplier;
    const quotationVersions = getQuotationVersions(supplierId);
    const getVersion = !_.isEmpty(versionData) ? versionData[0].versions : undefined;

    let eyeIcon = supplier?.isSeen;
    let seenAt = supplier?.seenAt;

    if (eyeIcon && seenAt) {
      seenAt = moment(seenAt).locale('sv').format('D MMMM YYYY HH:mm');
    }

    // push all relative versions of the supplier
    if (!_.isEmpty(getVersion)) {
      for (const qv of getVersion) {
        for (const s of qv.suppliers) {
          if (s.supplierId === supplierId._id) quotationVersions.push(qv);
        }
      }
    }

    const bidIntent = supplier?.bidIntentStatus;
    const getBidIntentTitle = () => {
      if (bidIntent?.intent === BidIntentStatus.ACCEPTED) {
        return t('statusIcon.title.accepted', 'Avser att lämna anbud');
      } else if (bidIntent?.intent === BidIntentStatus.DECLINED) {
        return t('statusIcon.title.declined', 'Avser att inte lämna anbud');
      }
      return '';
    };

    return (
      <div style={{display:"flex", justifyContent: "center", alignItems: "center", gap: "8px"}}>
        {eyeIcon ? (
          <TooltipContainer renderReferenceComponent={(className, ref) => <OpenedEyeIcon className={className} ref={ref} style={{ width: '16px', height: '16px', fill: 'var(--gray-700)' }} />}>
            <span style={{ whiteSpace: 'nowrap' }}>{t('statusIcon.tooltip.viewed', 'Leverantören har öppnat anbudssidan')}{seenAt ? ` (${seenAt})` : ''}</span>
          </TooltipContainer>
        ) : (
          <TooltipContainer renderReferenceComponent={(className, ref) => <OpenedEyeIcon className={className} ref={ref} style={{ width: '16px', height: '16px', fill: 'var(--gray-300)' }} />}>
            <span style={{ whiteSpace: 'nowrap' }}>{t('statusIcon.tooltip.notViewed', 'Anbudssidan ej öppnad')}</span>
          </TooltipContainer>
        )}
        {
          bidIntent?.intent && bidIntent.intent !== BidIntentStatus.UNDECIDED && bidIntentOptions[bidIntent.intent] ? (
            <TooltipContainer className='bidIntentTooltip' renderReferenceComponent={ (className, ref) =>
              ({
                [BidIntentStatus.ACCEPTED]: <CircleCheckIcon className={className} ref={ref} fill="var(--gray-700)" width='16px' height='16px' />,
                [BidIntentStatus.DECLINED]: <CircleXIcon className={className} ref={ref} fill="var(--gray-700)" width='16px' height='16px' />,
              }?.[bidIntent.intent as BidIntentStatus.ACCEPTED | BidIntentStatus.DECLINED] || null)
            }>
              <div>
                <p><strong>{getBidIntentTitle()}</strong></p>
                <p>
                  {t(
                    'statusIcon.tooltip.intent.details',
                    '{{name}} svarade den {{timestamp}}',
                    {
                      name: bidIntent?.declaredBy?.name,
                      timestamp: moment(bidIntent.declaredAt).locale('sv').format('D MMMM YYYY HH:mm')
                    }
                  )}
                </p>
              </div>
              {bidIntent?.reasonDeclined && (
                <div>
                  <p><strong>{t('statusIcon.tooltip.intent.label.declineReason', 'Orsak:')}</strong></p>
                  <p>{`${bidIntent?.reasonDeclined}`}</p>
                </div>
              )}
            </TooltipContainer>
          ) : (
            <TooltipContainer className='bidIntentTooltip' renderReferenceComponent={(className, ref) => <CircleQuestionIcon className={className} ref={ref} fill="var(--gray-300)" />}>
              <div><p>{t('statusIcon.tooltip.intent.noIntent', 'Ingen avsiktsförklaring')}</p></div>
            </TooltipContainer>
          )
        }
        {supplier.isSubmitted ? (
          <TooltipContainer className='bidIntentTooltip' renderReferenceComponent={(className, ref) => <CircleDollarIcon className={className} ref={ref} style={{color:"var(--gray-700)"}} />}>
            <div><p>{t('statusIcon.tooltip.bidSubmitted', 'Leverantören har lämnat anbud')}</p></div>
          </TooltipContainer>
        ) : (
          <TooltipContainer className='bidIntentTooltip' renderReferenceComponent={(className, ref) => <CircleDollarIcon className={className} ref={ref} style={{color:"var(--gray-300)"}}/>}>
            <div><p>{t('statusIcon.tooltip.noBidSubmitted', 'Inget anbud')}</p></div>
          </TooltipContainer>
        )}
      </div>
    );
  }

  const renderPriceFieldRows = (displayColumns: DisplayColumn[]) => {
    const prices = displayColumns.map(col => getPricesForVersionAndSupplier(col.supplier.supplierId._id, col.versionIdx));
    const distinctFields: string[] = Array.from(prices.reduce((set, pricesForSupplier) => {
      pricesForSupplier.map((price: HasName) => set.add(price.name));
      return set;
    }, new Set<string>()));
    return distinctFields.map(name =>
      <tr key={"pricerow_" + name}>
        <td key="name">{name}</td>
        {displayColumns.map((col, i) => {
          const price = prices[i].find((p: HasName) => p.name === name);
          return <td key={keyOfColumn(col) + "_price_" + name} {...colTdProps(col, styles.right)}>{price?.price}&nbsp;{price?.unit}</td>;
        })}
      </tr>
    );
  }

  const renderDemandRows = (displayColumns: DisplayColumn[]) => {
    const demands = displayColumns.map(col => getDemandsForVersionAndSupplier(col.supplier.supplierId._id, col.versionIdx));
    const distinctFields: string[] = Array.from(demands.reduce((set, demandsForSupplier) => {
      demandsForSupplier.map((demand: HasName) => set.add(demand.name));
      return set;
    }, new Set<string>()));
    return distinctFields.map(name =>
      <tr key={"demandrow_" + name}>
        <td key="name">{name}</td>
        {displayColumns.map((col, i) => {
          const demand = demands[i].find((p: HasName) => p.name === name);
          if (demand && demand.value !== undefined) {
            return <td key={keyOfColumn(col) + "_demand_" + name} {...colTdProps(col)}>
              <div className={styles.center}>
                {demand.value ? (
                  <div className="collective-agreementFlag true"><CheckIcon /></div>
                ) : (
                  <div className="collective-agreementFlag false"><CloseIcon /></div>
                )}
              </div>
            </td>;
          }
          return <td key={keyOfColumn(col) + "_demand_" + name} {...colTdProps(col)}>&nbsp;</td>;
        })}
      </tr>
    );
  }

  const renderExtendDateButton = (supplier: Supplier) => {
    const { supplierId } = supplier;
    const versionInState = getVersionInState(supplierId._id);
    const quotationVersions = getQuotationVersions(supplierId);
    const getVersion = !_.isEmpty(versionData) ? versionData[0].versions : undefined;

    // push all relative versions of the supplier
    if (!_.isEmpty(getVersion)) {
      for (const qv of getVersion) {
        for (const s of qv.suppliers) {
          if (s.supplierId === supplierId._id) quotationVersions.push(qv);
        }
      }
    }
    return (
      <TooltipContainer
        renderReferenceComponent={(className, ref) =>
          <BtButton
            ref={ref}
            class={className}
            color="white"
            size="xxs"
            onClick={() => onCalendar(supplier, quotationVersions, getVersion[versionInState])}
          >
            <MoreTimeIcon color='var(--gray-700)' size={18} />
          </BtButton>
        }
      >
        <span style={{ whiteSpace: 'nowrap' }}>{t('extendDate.tooltip', 'Förläng anbudstiden')}</span>
      </TooltipContainer>
    );
  }

  const renderMessage = (supplier: Supplier) => {
    return supplier.message && (
      <TooltipContainer renderReferenceComponent={(className, ref) => <MessageIcon className={className} ref={ref} style={{ top: '2px', width: '18px', fill: 'var(--gray-700)'}}/>}>
        <div style={{ minWidth: '250px' }}>
          <strong>{t('message.label', 'Meddelande:')}</strong>
          <HTMLParser data={supplier.message}/>
        </div>
      </TooltipContainer>
    );
  };

  const renderReservations = (supplier: Supplier) => {
    return supplier.reservations && (
      <TooltipContainer renderReferenceComponent={(className, ref) => <TriangleWarningIcon className={className} ref={ref} style={{ top: '2px', width: '19px', fill: 'var(--gray-700)'}}/>}>
        <div style={{ minWidth: '250px' }}>
          <strong>{t('reservations.label', 'Reservationer:')}</strong>
          <HTMLParser data={supplier.reservations}/>
        </div>
      </TooltipContainer>
    );
  };

  const renderAttachments = (supplier: Supplier, versionIdx: number) => {
    const projectSlug = currentProject.slug;
    const projectName = currentProject.name;
    const supplierId = supplier.supplierId._id;
    const supplierName = supplier.supplierId.name;
    const quoteVersionNo = versionIdx;
    return (<div style={{display:"grid",justifyContent:"center"}}>
      {supplier?.attachments.length > 0 && (
        <TooltipContainer renderReferenceComponent={(className, ref) =>
          <DownloadButton
            url={ServiceQuotation.getDownloadQuotationResponseFilesInZipURL({
              projectSlug,
              areaSlug,
              supplierId,
              versionNumber: quoteVersionNo,
              orgId: currentProject.orgId,
            })}
            fileName={t('attachments.fileName', 'Anbud från {{supplierName}} på {{projectName}} - {{areaName}} - V{{version}}.zip', { supplierName, projectName, areaName, version: quoteVersionNo + 1 })}
            render={({ downloading, progress, onClick }) => (
              downloading ? (
                <BtButton class={className} ref={ref} color="white" size="xxs" disabled={true}>
                  <Spinner progress={progress} size="xxs" />
                </BtButton>
              ) : (
                <BtButton class={className} ref={ref} color="white" size="xxs" onClick={onClick}>
                  <DownloadIcon/>
                </BtButton>
              )
            )}
          />
        }>
          <div style={{ minWidth: '250px' }}>
            <strong>{t('attachments.title', 'Bilagor (ladda ner alla som .zip):')}</strong>
            <ul>
              {supplier.attachments.map(a => <li key={"attachment_" + a._id}>{a.fileName}</li>)}
            </ul>
          </div>
        </TooltipContainer>
      )}
    </div>);
  }

  const renderOtherContent = (supplier: Supplier, versionIdx: number) => {
    return <div style={{display:"flex", gap: 16, justifyContent: "center"}}>
      {renderMessage(supplier)}
      {renderReservations(supplier)}
      {renderAttachments(supplier, versionIdx)}
    </div>;
  }

  const renderDaysLeft = (date: Date | null) => {
    const days = calcDaysLeft(date);
    return days < 0 ? t('daysOverdue', '{{count}} dagar sedan', { count: -days }) : t('daysLeft', '{{count}} dagar kvar', { count: days });
  };

  const renderDueDate = (date: Date) => {
    return t(
      'dueDate',
      'Anbudstiden går ut {{date}}',
      { context: calcDaysLeft(date) < 0 ? 'overdue' : null, date: moment(date).format("YYYY-MM-DD") }
    );
  };

  const renderColumnBasedTable = () => {
    const suppliers = modifiedSuppliers?.sort((a, b) => a?.name?.toLowerCase() < b?.name?.toLowerCase() ? -1 : 1);
    const displayColumns = getDisplayColumns(suppliers);
    return (
      <table className={styles.table}>
        <thead>
          <tr>
            <th>&nbsp;</th>
            {suppliers.map((supplier, supplierIndex) => {
              const supplierId = supplier.supplierId._id;
              const versions = getQuotationVersions(supplier.supplierId);
              const isExpanded = expandedSuppliers.has(supplierId);
              const colSpan = isExpanded && versions.length > 1 ? versions.length : 1;

              return (
                <th key={supplierId} className={cx(styles.supplierName, { [`${styles.colGray}`]: supplierIndex % 2 === 0, [`${styles.columnHovered}`]: hoveredSupplierIndex === supplierIndex })} colSpan={colSpan}>
                  <div className={styles.center} style={{gap: "8px"}}>
                    <span>
                      {supplier.supplierId.name}
                    </span>
                    <TooltipContainer
                      renderReferenceComponent={(className, ref) =>
                        <BtButton
                          ref={ref}
                          class={className}
                          color="white"
                          size="xxs"
                          onClick={() => history.push(supplier.bidLink, {
                            supplierId: supplier.supplierId._id
                          })}
                        >
                          <RequestQuoteIcon size={18} color='var(--gray-700)' />
                        </BtButton>
                      }
                    >
                      <span style={{ whiteSpace: 'nowrap' }}>{t('versionSelector.tooltip', 'Öppna anbudsformuläret')}</span>
                    </TooltipContainer>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>{t('table.header.version', 'Version')}</th>
            {displayColumns.map((col) => {
              const { isLatest, supplier } = col;
              const supplierId = supplier.supplierId._id;
              const isExpanded = expandedSuppliers.has(supplierId);
              const versions = getQuotationVersions(supplier.supplierId);
              const canExpand = versions.length > 1;

              return (
              <td key={keyOfColumn(col)} {...colTdProps(col)}>
                <div className={styles.center}>
                  <div className={styles.center}>
                    <span style={{ fontWeight: 600 }}>{`V${col.versionIdx + 1}`}</span>
                    {isLatest && canExpand && (
                      <BtButton
                        class='white'
                        style={{ width: 'fit-content' }}
                        onClick={() => toggleExpandSupplier(supplierId)}
                      >
                        {isExpanded ? t('buttons.collapseSingle', 'Dölj äldre') :  t('buttons.expandSingle', 'Visa alla')}
                      </BtButton>
                    )}
                  </div>
                </div>
              </td>
            )})}
          </tr>
          <tr>
            <th>{t('table.header.dueDate', 'Anbudstid')}</th>
            {displayColumns.map((col) => {
              const supplierData = getSupplierDataForVersion(col.supplier, col.versionIdx);
              return (
                <td key={keyOfColumn(col)} {...colTdProps(col)}>
                  <div className={styles.center}>
                    {col.isLatest && supplierData.date && (
                      <div style={{display: 'flex', justifyContent: 'center'}}>
                        <TooltipContainer renderReferenceComponent={(className, ref) =>
                          <div className={className} ref={ref} style={{whiteSpace: 'nowrap'}}>
                            {renderDaysLeft(supplierData?.date)}
                          </div>
                        }>
                          <div style={{minWidth: 250}}>
                            {renderDueDate(supplierData.date)}
                          </div>
                        </TooltipContainer>
                      </div>
                    )}
                    {col.isLatest && renderExtendDateButton(col.supplier)}
                  </div>
                </td>
              );
            })}
          </tr>
          <tr>
            <th>{t('table.header.status', 'Status')}</th>
            {displayColumns.map((col) => {
              const supplierData = getSupplierDataForVersion(col.supplier, col.versionIdx);
              return (
                <td key={keyOfColumn(col)} {...colTdProps(col)}>
                  {renderStatusIcons(supplierData)}
                </td>
              );
            })}
          </tr>
          <tr className={styles.empty}>
            <th>&nbsp;</th>
            {displayColumns.map((col) => <td key={keyOfColumn(col)} {...colTdProps(col)}>&nbsp;</td>)}
          </tr>
          <tr className={styles.empty}>
            <th>{t('table.header.priceFields', 'Prisfält')}</th>
            {displayColumns.map((col) => <td key={keyOfColumn(col)} {...colTdProps(col)}>&nbsp;</td>)}
          </tr>
          {renderPriceFieldRows(displayColumns)}
          <tr className={styles.empty}>
            <th>&nbsp;</th>
            {displayColumns.map((col) => <td key={keyOfColumn(col)} {...colTdProps(col)}>&nbsp;</td>)}
          </tr>
          <tr className={styles.empty}>
            <th>{t('table.header.requirements', 'Skallkrav')}</th>
            {displayColumns.map((col) => <td key={keyOfColumn(col)} {...colTdProps(col)}>&nbsp;</td>)}
          </tr>
          {renderDemandRows(displayColumns)}
          <tr className={styles.empty}>
            <th>&nbsp;</th>
            {displayColumns.map((col) => <td key={keyOfColumn(col)} {...colTdProps(col)}>&nbsp;</td>)}
          </tr>
          <tr>
            <th>{t('table.header.otherContent', 'Övrigt innehåll')}</th>
            {displayColumns.map((col) => {
              const supplierData = getSupplierDataForVersion(col.supplier, col.versionIdx);
              return (
                <td key={keyOfColumn(col)} {...colTdProps(col)}>
                  {renderOtherContent(supplierData, col.versionIdx)}
                </td>
              );
            })}
          </tr>
          {showDefaultBidValidityDate && (
            <tr>
              <th>{t('table.header.validity', 'Giltigt till')}</th>
              {displayColumns.map((col) => {
                const supplierData = getSupplierDataForVersion(col.supplier, col.versionIdx);
                return (
                  <td key={keyOfColumn(col)} {...colTdProps(col)}>
                    <div className={styles.center}>{getQuoteValidity(supplierData?.quotationValidity)}</div>
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    )
  }

  return (
    <div className={styles.container}>
      <div style={{display: "flex", alignItems: "center"}}>
        <p style={{marginRight: "16px"}}>{t('buttons.description', 'Visa anbud')}</p>
        <FilteringControl
          value={''}
          className={styles.filteringControlContainer}
          options={[
            {
              label: t('buttons.collapseAll', 'Senaste versionen för varje leverantör'),
              value: 'all',
              onSelection: () => {
                modifiedSuppliers.map(s => {
                  if(expandedSuppliers.has(s.supplierId._id)) toggleExpandSupplier(s.supplierId._id);
                });
              },
              disabled: isCollapseAllEnabled
            },
            {
              label: t('buttons.expandAll', 'Alla versioner'),
              value: 'latest',
              onSelection: () => {
                modifiedSuppliers.map(s => {
                  if(!expandedSuppliers.has(s.supplierId._id)) toggleExpandSupplier(s.supplierId._id);
                });
              },
              disabled: isExpandAllEnabled
            }, 
          ]}
        />
      </div>
      {renderColumnBasedTable()}
    </div>
  )
}

export default withRouter(BidOverview);
